/**
 * OperatorMailForm - one-off operator mail to a customer contact (OCP-649).
 *
 * Sits on the tenant and account detail pages. On open it runs a dry run
 * (`POST /v1/admin/mail/operator/send` with `dry_run: true`) so the
 * operator sees the resolved recipient, the From and the Reply-To the
 * mail will carry (the OCP-636 policy - no personal address) plus any
 * warning (suppressed, rate limit) BEFORE writing. The real send needs
 * subject, message and a reason (audited `mail.operator_send`).
 *
 * The same endpoint is what `ocp_send_operator_mail` calls - this form
 * is the browser face of it, not a separate path.
 *
 * OCP-928 - the body names its format (plain text, Markdown, HTML).
 * "Preview mail" asks the API to render exactly the HTML and text part the
 * send will carry (`/render`, shown in a sandboxed frame); "Send test to
 * me" mails that render to the operator's own address (`/test-send`).
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../lib/api';
import { MAIL_FORMAT_HINT, MailFormatSelect, MailRenderPreview, type MailBodyFormat } from './MailRenderPreview';

export type OperatorMailTarget = { tenant_id: string } | { account_id: string };

interface RenderedMail {
  format: MailBodyFormat;
  locale: 'de' | 'en';
  subject: string;
  html: string;
  text: string;
  to: string | null;
}

interface TestSendResult {
  to: string;
  message_id: string;
  rate_limit: { used: number; limit: number };
}

interface OperatorMailResult {
  sent: boolean;
  dry_run: boolean;
  message_id: string | null;
  to: string;
  resolved_from: 'tenant' | 'account' | 'email';
  locale: 'de' | 'en';
  from: string;
  reply_to: string | null;
  warnings: string[];
  rate_limit: { used: number; limit: number; window_seconds: number };
}

interface ErrorBody {
  error?: string;
  reason?: string | null;
  retry_after_seconds?: number;
  limit?: number;
}

function describeError(err: unknown): string {
  const body = err instanceof ApiError ? ((err.body as ErrorBody | null) ?? {}) : {};
  switch (body.error) {
    case 'suppressed':
      return `This address is on the suppression list${body.reason ? ` (${body.reason})` : ''} - orboto Mail will not deliver to it.`;
    case 'rate_limited':
      return `Rate limit reached (${body.limit ?? 10} operator mails per hour). Try again in ${Math.ceil((body.retry_after_seconds ?? 0) / 60)} min.`;
    case 'no_contact_email':
      return 'No contact email on this record.';
    case 'tenant_hard_deleted':
      return 'This tenant is deleted - no mail.';
    case 'from_domain_not_operator':
      return 'The configured From address is not on the orboto Mail operator allowlist - check mail.from_address.';
    case 'oms_unavailable':
      return 'orboto Mail is unreachable right now - try again in a minute.';
    case 'not_wired':
      return 'Operator mail is not configured on this api.';
    case 'empty_body':
      return 'The message renders to no readable text (an HTML body of images or scripts only). Every mail needs a readable text part.';
    case 'no_operator_address':
      return 'Your operator account has no mailbox address for a test send.';
    default:
      return `Could not send${body.error ? ` (${body.error})` : ''}.`;
  }
}

export function OperatorMailForm({ target, onSent }: { target: OperatorMailTarget; onSent?: () => Promise<void> | void }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<OperatorMailResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [format, setFormat] = useState<MailBodyFormat>('text');
  const [reason, setReason] = useState('');
  const [locale, setLocale] = useState<'' | 'de' | 'en'>('');
  const [busy, setBusy] = useState<'' | 'send' | 'render' | 'test'>('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rendered, setRendered] = useState<RenderedMail | null>(null);

  // A preview belongs to exactly the inputs it was rendered from.
  useEffect(() => {
    setRendered(null);
  }, [subject, text, format, locale]);

  const tenantId = 'tenant_id' in target ? target.tenant_id : null;
  const accountId = 'account_id' in target ? target.account_id : null;

  const loadPreview = useCallback(async () => {
    setPreviewError(null);
    try {
      const r = await api<OperatorMailResult>('/v1/admin/mail/operator/send', {
        method: 'POST',
        body: { to: tenantId ? { tenant_id: tenantId } : { account_id: accountId }, dry_run: true },
      });
      setPreview(r);
    } catch (err) {
      setPreview(null);
      setPreviewError(describeError(err));
    }
  }, [tenantId, accountId]);

  useEffect(() => {
    if (open) void loadPreview();
  }, [open, loadPreview]);

  function content() {
    return {
      to: target,
      subject: subject.trim(),
      body: text.trim(),
      format,
      ...(locale ? { locale } : {}),
    };
  }

  async function send() {
    setBusy('send');
    setNote(null);
    setError(null);
    try {
      const r = await api<OperatorMailResult>('/v1/admin/mail/operator/send', {
        method: 'POST',
        body: { ...content(), reason: reason.trim() },
      });
      setNote(`Sent to ${r.to}${r.message_id ? ` - message ${r.message_id}` : ''} (${r.rate_limit.used}/${r.rate_limit.limit} this hour).`);
      setSubject('');
      setText('');
      setReason('');
      await onSent?.();
      await loadPreview();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy('');
    }
  }

  // OCP-928 - the server renders exactly what `send` will hand to the mailer.
  async function renderPreview() {
    setBusy('render');
    setError(null);
    try {
      setRendered(await api<RenderedMail>('/v1/admin/mail/operator/render', { method: 'POST', body: content() }));
    } catch (err) {
      setRendered(null);
      setError(describeError(err));
    } finally {
      setBusy('');
    }
  }

  async function testSend() {
    setBusy('test');
    setNote(null);
    setError(null);
    try {
      const r = await api<TestSendResult>('/v1/admin/mail/operator/test-send', { method: 'POST', body: content() });
      setNote(`Test sent to your address ${r.to} - message ${r.message_id} (${r.rate_limit.used}/${r.rate_limit.limit} test mails this hour).`);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy('');
    }
  }

  const hasContent = subject.trim().length > 0 && text.trim().length > 0;
  const canSend = !busy && Boolean(preview) && hasContent && reason.trim().length > 0;

  if (!open) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Operator mail</div>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          One audited mail to this contact, From / Reply-To per the operator mail settings.
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="operator-mail-open"
          className="mt-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Send mail
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm" data-testid="operator-mail-form">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Operator mail</div>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:underline">
          close
        </button>
      </div>
      {preview && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs" data-testid="operator-mail-preview">
          <dt className="text-gray-500">To</dt>
          <dd className="font-mono text-gray-900 dark:text-gray-100">
            {preview.to} <span className="text-gray-500">({preview.resolved_from} contact, {preview.locale})</span>
          </dd>
          <dt className="text-gray-500">From</dt>
          <dd className="font-mono text-gray-900 dark:text-gray-100">{preview.from}</dd>
          <dt className="text-gray-500">Reply-To</dt>
          <dd className="font-mono text-gray-900 dark:text-gray-100">{preview.reply_to ?? '(none)'}</dd>
          <dt className="text-gray-500">Limit</dt>
          <dd className="text-gray-700 dark:text-gray-300">
            {preview.rate_limit.used}/{preview.rate_limit.limit} this hour
          </dd>
        </dl>
      )}
      {preview?.warnings.includes('unknown_contact') && (
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">This address is not a known tenant contact or portal user.</p>
      )}
      {previewError && (
        <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">
          {previewError}
        </p>
      )}
      <div className="mt-2 space-y-2">
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          maxLength={200}
          aria-label="Subject"
          className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
        />
        <div className="flex flex-wrap items-center gap-2">
          <MailFormatSelect value={format} onChange={setFormat} />
          <span className="text-[11px] text-gray-500" data-testid="operator-mail-format-hint">
            {MAIL_FORMAT_HINT[format]}
          </span>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Message in the chosen format. Rendered inside the orboto mail shell (logo, footer, unsubscribe)."
          rows={6}
          maxLength={format === 'html' ? 200000 : 20000}
          aria-label="Message"
          className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-mono"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void renderPreview()}
            disabled={busy !== '' || !hasContent}
            data-testid="operator-mail-render"
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {busy === 'render' ? 'Rendering...' : 'Preview mail'}
          </button>
          <button
            type="button"
            onClick={() => void testSend()}
            disabled={busy !== '' || !hasContent}
            data-testid="operator-mail-test-send"
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {busy === 'test' ? 'Sending test...' : 'Send test to me'}
          </button>
        </div>
        {rendered && <MailRenderPreview mail={rendered} label={`Preview (${rendered.format}, ${rendered.locale})`} testId="operator-mail-rendered" />}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (audit)"
            maxLength={2000}
            aria-label="Reason"
            className="w-48 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
          />
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as '' | 'de' | 'en')}
            aria-label="Shell language"
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
          >
            <option value="">shell language: contact default</option>
            <option value="de">de</option>
            <option value="en">en</option>
          </select>
          <button
            type="button"
            onClick={() => void send()}
            disabled={!canSend}
            data-testid="operator-mail-send"
            className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {busy === 'send' ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        One recipient, audited. Replies land at the Reply-To above (mail.reply_to_address), never at a personal address. Campaigns are not this.
      </p>
      {note && <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">{note}</p>}
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}
